import { canCreateTaskManually } from './task-creation';
import { z } from 'zod';
import { ApiKey } from './ids';
import type { ModelIndex } from './model-index';
import type { ObjectType } from './metamodel';

export const OBJECT_ACTIVITIES = ['note', 'call', 'email', 'meeting', 'task'] as const;
export type ObjectActivity = string;
export const DuplicateRule = z
  .object({
    fields: z
      .array(ApiKey)
      .min(1)
      .max(10)
      .refine((v) => new Set(v).size === v.length, 'Choose each field once'),
    mode: z.enum(['exact', 'fuzzy']).default('exact'),
  })
  .strict();
export const ObjectBehaviorShape = z
  .object({
    activities: z
      .array(ApiKey)
      .max(100)
      .refine((v) => new Set(v).size === v.length, 'Choose each activity once')
      .nullable(),
    /** null allows all task types; [] allows none. Keys are workspace task type keys. */
    taskTypes: z
      .array(ApiKey)
      .max(100)
      .refine((v) => new Set(v).size === v.length, 'Choose each task type once')
      .nullable(),
    duplicates: z
      .object({
        enabled: z.boolean(),
        /** Compatibility for the existing weighted person/company detector, never inferred from semantics after migration. */
        legacy: z.boolean().default(false),
        rules: z.array(DuplicateRule).max(20).default([]),
      })
      .strict(),
  })
  .strict();
/** Stored snapshots and older packages may contain retired settings; discard them on read. */
export const ObjectBehavior = ObjectBehaviorShape.strip();
/** Writes expose only current settings, with a typed JSON Schema for the workspace agent. */
export const PartialObjectBehavior = ObjectBehaviorShape.partial();
export type ObjectBehavior = z.infer<typeof ObjectBehavior>;
export function legacyObjectBehavior(traits: readonly string[] = []): ObjectBehavior {
  return {
    activities: null,
    taskTypes: null,
    duplicates: {
      enabled: true,
      legacy: traits.some((t) => t === 'person' || t === 'organization'),
      rules: [],
    },
  };
}
export function behaviorOf(object: { traits: readonly string[]; behavior?: ObjectBehavior }): ObjectBehavior {
  return object.behavior ?? legacyObjectBehavior(object.traits);
}
export function activityAllowed(
  object: { traits: readonly string[]; behavior?: ObjectBehavior },
  activity: ObjectActivity,
): boolean {
  const behavior = behaviorOf(object);
  if (activity === 'task' && behavior.taskTypes?.length === 0) return false;
  const activities = behavior.activities;
  return activities === null || activities.includes(activity);
}
export function taskTypeAllowed(
  object: { traits: readonly string[]; behavior?: ObjectBehavior },
  type: string,
): boolean {
  const b = behaviorOf(object);
  return (
    (b.activities === null || b.activities.includes('task')) &&
    (b.taskTypes === null || b.taskTypes.includes(type))
  );
}
/** Manual record tasks also need the capability used by their communication action. */
export function manualTaskTypeAllowed(
  object: { traits: readonly string[]; behavior?: ObjectBehavior },
  type: { key: string; action?: string; manualCreation?: boolean; system?: boolean; archived?: boolean },
): boolean {
  return (
    canCreateTaskManually(type) &&
    taskTypeAllowed(object, type.key) &&
    (!['call', 'email'].includes(type.action ?? '') || activityAllowed(object, type.action!))
  );
}
/** Pipeline semantics come from an active stage field bound to a pipeline, not from a trait or arbitrary status column. */
export function hasPipeline(model: ModelIndex, object: ObjectType): boolean {
  return model
    .fieldsOf(object.id)
    .some(
      (f) =>
        !f.archivedAt &&
        (f.type === 'status' || f.type === 'stage') &&
        model.pipelines.some((p) => p.objectTypeId === object.id && p.fieldId === f.id),
    );
}
export const RETIRED_OBJECT_TRAITS = [
  'pipeline',
  'timeline',
  'schedulable',
  'catalog',
  'enrichable',
] as const;
export const semanticTraits = (traits: readonly string[]) =>
  traits.filter((t) => !RETIRED_OBJECT_TRAITS.includes(t as (typeof RETIRED_OBJECT_TRAITS)[number]));
